import { GMI, STATE } from "../../constants";
import replacement from "../../services/gma/replacement";
import { formatTimeStamp } from "../../utils/app/reservations";
import { types } from "../../utils/js/types";
import request from "../request";

const TIMESTAMP = "timestamp";
const DATE = "date";
const TIME = "time";

const { REPLACEMENT } = STATE.UI;

/**
 * @private
 * @name setPickupTimeStamp
 * @param {String} timeStamp
 * @returns {Object}
 */
function setPickupTimeStamp(timeStamp) {
  return {
    type: GMI.SET_GMI_STATE,
    path: REPLACEMENT.PICKUP_DATE.PICKUP_TIME,
    value: timeStamp,
  };
}

/**
 * @private
 * @desc DRY, Function is used to simplify setPickupDate and setPickupTime
 * @param {Object} opts {timeStamp, part}
 * @returns {Function}
 */
function batchPickupDateOrTime(opts) {
  const { timeStamp, part } = opts;
  return (dispatch, getState) => {
    const value = formatTimeStamp({
      ts: timeStamp,
      state: getState(),
      part,
      path: REPLACEMENT.PICKUP_DATE.PICKUP_TIME,
    }).ts;
    dispatch({
      type: GMI.SET_GMI_STATE,
      path: REPLACEMENT.PICKUP_DATE.PICKUP_TIME,
      value,
    });
  };
}

/**
 * @private
 * @name setPickupDate
 * @desc Set replacement pickup date timestamp by date part
 * @param {String} timeStamp
 * @returns {Function}
 */
function setPickupDate(timeStamp) {
  return (dispatch) =>
    dispatch(batchPickupDateOrTime({ timeStamp, part: DATE }));
}

/**
 * @private
 * @name setPickupTime
 * @desc Set replacement pickup date timestamp by time part
 * @param {string} timeStamp
 * @returns {Function}
 */
function setPickupTime(timeStamp) {
  return (dispatch) =>
    dispatch(batchPickupDateOrTime({ timeStamp, part: TIME }));
}

/**
 * @desc Gets data from replacement.ticket.pickup_time to be saved to gmi.ui.replacement.pickupDate.pickup_time state
 * @param {Immutable} session - GMA
 * @returns {Object}
 */
export function getPickupTimeState(session) {
  const pickupTime = session.getIn(["replacement", "ticket", "pickup_time"]);

  if (!pickupTime) return;

  return pickupTime;
}

/**
 * Action creator for setting pickup time state
 * @private
 * @param {string} value - Value to set
 * @returns {Object} Action object
 */
const setPickupTimeState = (value) => ({
  type: GMI.SET_GMI_STATE,
  path: REPLACEMENT.PICKUP_DATE.PATH,
  value,
});

/**
 * Action creator for clearing pickup time state
 * @private
 * @param {string} value - Value to set
 * @returns {Object} Action object
 */
const clearPickupTimeState = (value) => ({
  type: GMI.SET_GMI_STATE,
  path: REPLACEMENT.PICKUP_DATE.PATH,
  value,
});

const pickupTimeState = {
  /**
   * @name set
   * @desc A global setter for ui.replacement.pickupDate.pickup_time
   * @param {String} [type] - Type of the field to set
   * @param {Object} [args] - Value to set
   * @returns {Function} Dispatch function
   * @memberof actions.replacement
   * @example
   *
   * // Set pickup_time
   * store.dispatch(actions.replacement.pickupDatePickupTimeState.set(null, {
   *  "pickup_time": "2024-05-31T13:00:00"
   * }))
   *`
   */
  set(type = null, args = {}) {
    return (dispatch) => {
      if (!types(type).isString) {
        dispatch(setPickupTimeState(args));
      } else if (type === TIMESTAMP) {
        dispatch(setPickupTimeStamp(args.timestamp));
      } else if (type === DATE) {
        dispatch(setPickupDate(args.timestamp));
      } else if (type === TIME) {
        dispatch(setPickupTime(args.timestamp));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for ui.replacement.pickupDate.pickup_time
   * @param {String} [type] - Type of the field to set
   * @returns {Function} Dispatch function
   * @memberof actions.replacement
   * @example
   * // Unset pickup_time
   * store.dispatch(actions.replacement.pickupDatePickupTimeState.unset())
   */
  unset(type = null) {
    return (dispatch) => {
      if (!types(type).isString) {
        dispatch(clearPickupTimeState());
      }
    };
  },
};

/**
 * Calls PickupDate service with location date&time (pickup_time) payload
 * @param {Object} object
 * @example store.dispatch(actions.replacement.pickupDate.submit());
 * @returns {Function} Dispatched service action
 */
function submit() {
  return (dispatch, getState) => {
    const data = getState().getIn(REPLACEMENT.PICKUP_DATE.PATH).toJS();

    return dispatch(
      request({
        service: replacement.pickupDate,
        data,
      })
    );
  };
}

export default {
  pickupTimeState,
  submit,
};
